import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";
import { Checkbox } from "@/components/ui/Field";
import { router } from "@inertiajs/react";

type Row = {
  id: number;
  event_name: string | null;
  name: string;
  email: string | null;
  title: string;
  code_url: string | null;
  playable_url: string | null;
  github_username: string | null;
  problems: string[];
  links_requested_at: string | null;
};

type Counts = {
  reviewed: number;
  ready: number;
  blocked: number;
  askable: number;
  unreviewed_us: number;
};

// Every reviewed site in the shape Hack Club's Unified YSWS database takes.
// HQ only: the CSV carries each kid's name, birthday and home address.
export default function AdminUnified({
  rows,
  counts,
  problem_counts,
  missing_only,
}: {
  rows: Row[];
  counts: Counts;
  problem_counts: Record<string, number>;
  missing_only: boolean;
}) {
  const askKids = () =>
    router.post("/admin/unified/request_links", {}, { preserveScroll: true });

  return (
    <Layout>
      <div className="flex w-full flex-col gap-6 py-6">
        <PageHeading
          title="Unified"
          subtitle="Every reviewed site, ready for Hack Club's Unified YSWS database."
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Panel title="reviewed.count">
            <p className="font-display text-display text-ink font-bold">
              {counts.reviewed}
            </p>
            <p className="text-caption text-ink-soft">
              Approved by a reviewer. {counts.unreviewed_us} approved US sites
              still need a look first.
            </p>
          </Panel>
          <Panel title="ready.count">
            <p className="font-display text-display text-ink font-bold">
              {counts.ready}
            </p>
            <p className="text-caption text-ink-soft">
              Nothing wrong. These are the only rows in the Unified CSV.
            </p>
          </Panel>
          <Panel title="blocked.count">
            <p className="font-display text-display text-ink font-bold">
              {counts.blocked}
            </p>
            <p className="text-caption text-ink-soft">
              Held back until fixed. {counts.askable} haven't been asked for
              links yet.
            </p>
          </Panel>
        </div>

        {Object.keys(problem_counts).length > 0 && (
          <Panel title="what-is-blocking.txt">
            <ul className="text-body text-ink flex flex-col gap-1">
              {Object.entries(problem_counts).map(([problem, count]) => (
                <li key={problem} className="flex justify-between gap-4">
                  <span>{problem}</span>
                  <span className="font-bold tabular-nums">{count}</span>
                </li>
              ))}
            </ul>
          </Panel>
        )}

        <Panel title="actions.txt" tone="alt">
          <div className="flex flex-wrap items-center gap-4">
            <a href="/admin/unified.csv" className="text-link underline">
              Download the Unified CSV ({counts.ready} ready rows)
            </a>
            <a href="/admin/unified.csv?all=1" className="text-link underline">
              Download every row with its problems
            </a>
            <Button size="sm" disabled={counts.askable === 0} onClick={askKids}>
              Email {counts.askable} {counts.askable === 1 ? "kid" : "kids"} for
              GitHub links
            </Button>
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={missing_only}
                onChange={(e) =>
                  router.get(
                    "/admin/unified",
                    e.target.checked ? { missing: "1" } : {},
                    { preserveState: true, replace: true },
                  )
                }
              />
              <span className="text-body text-ink">
                Only ones with a problem
              </span>
            </label>
          </div>
        </Panel>

        {rows.length === 0 ? (
          <EmptyState
            title="Nothing here"
            description="Sites show up once a reviewer approves them."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="text-caption text-ink w-full border-collapse">
              <thead>
                <tr className="border-line border-b-2 text-left">
                  <th className="py-2 pr-4">Site</th>
                  <th className="py-2 pr-4">Kid</th>
                  <th className="py-2 pr-4">Code</th>
                  <th className="py-2 pr-4">Live</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-line/20 border-b align-top"
                  >
                    <td className="py-2 pr-4">
                      <div className="font-bold">{row.title}</div>
                      <div className="text-ink-soft">{row.event_name}</div>
                    </td>
                    <td className="py-2 pr-4">
                      <div>{row.name}</div>
                      <div className="text-ink-soft break-all">{row.email}</div>
                    </td>
                    <td className="py-2 pr-4 break-all">
                      {row.code_url ? (
                        <a
                          href={row.code_url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="text-link underline"
                        >
                          {row.github_username ?? "Code page"}
                        </a>
                      ) : (
                        <Badge tone="warning">
                          {row.links_requested_at ? "Asked" : "Missing"}
                        </Badge>
                      )}
                    </td>
                    <td className="py-2 pr-4 break-all">
                      {row.playable_url ? (
                        <a
                          href={row.playable_url}
                          target="_blank"
                          rel="noopener noreferrer nofollow ugc"
                          className="text-link underline"
                        >
                          {row.playable_url}
                        </a>
                      ) : (
                        <Badge tone="warning">Missing</Badge>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      {row.problems.length === 0 ? (
                        <Badge tone="success">Ready</Badge>
                      ) : (
                        <ul className="text-sunset-orange flex flex-col gap-0.5">
                          {row.problems.map((problem) => (
                            <li key={problem}>{problem}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
